import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getLocalizedString } from "@icarius-localization/strings";
import { Chip, FormControl, IconButton, InputLabel, MenuItem, Select, Tooltip, Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import CancelIcon from "@material-ui/icons/Cancel";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HelpIcon from "@material-ui/icons/Help";
import NotInterestedIcon from "@material-ui/icons/NotInterested";
import LaunchIcon from "@material-ui/icons/Launch";
import { getAppColor } from "@icarius-app/selectors";
import Container from "../../../../container";

const Resolutions = (props) => {
    const { data, handleClick } = props;

    const [filter, setFilter] = useState("");

    const color = useSelector(getAppColor);

    const filteredResolutions = useMemo(() => {
        if (!data?.length) return [];
        if (!filter) return data;
        return data.filter((resolution) => {
            if (filter === "solved" && resolution.solved) return true;
            if (filter === "notSolved" && !resolution.solved) return true;
            if (filter === "passed" && resolution.passed) return true;
            if (filter === "notPassed" && resolution.passed === false) return true;
            if (filter === "toGrade" && resolution.solved && resolution.passed === null) return true;
            return false;
        });
    }, [filter, data]);

    return (
        <>
            <FormControl style={{ width: "100%", marginBottom: 5 }}>
                <InputLabel shrink id={"filterLabel"}>
                    {getLocalizedString("filter")}
                </InputLabel>
                <Select
                    displayEmpty
                    value={filter}
                    labelId={"filterLabel"}
                    id={"filter"}
                    onChange={(e) => setFilter(e.target.value)}
                    margin={"none"}
                >
                    {[
                        { value: getLocalizedString("allMale"), key: "" },
                        { value: getLocalizedString("solvedPlural"), key: "solved" },
                        { value: getLocalizedString("notSolved"), key: "notSolved" },
                        { value: getLocalizedString("toGrade"), key: "toGrade" },
                        { value: getLocalizedString("approvedPlural"), key: "passed" },
                        { value: getLocalizedString("failedPlural"), key: "notPassed" },
                    ].map((item) => (
                        <MenuItem className={"whiteText"} key={item.key} value={item.key}>
                            {item.value}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            {!filteredResolutions.length && (
                <div style={{ width: "100%" }}>
                    <Alert variant="filled" severity="info">
                        {getLocalizedString("noResolutionsMatchFilter")}
                    </Alert>
                </div>
            )}
            {filteredResolutions.map((resolution) => {
                const canGradeOrView = resolution.solved;

                return (
                    <div
                        key={resolution.code}
                        onClick={() => {
                            if (!canGradeOrView) return;
                            handleClick(resolution.code);
                        }}
                        style={{ width: "100%" }}
                    >
                        <Container
                            style={{
                                width: "100%",
                                color: "var(--mainText)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 10,
                                cursor: canGradeOrView ? "pointer" : "",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                }}
                            >
                                <div>
                                    {!resolution.solved && (
                                        <Tooltip title={getLocalizedString("notSolved")}>
                                            <NotInterestedIcon
                                                style={{
                                                    height: 20,
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                    {resolution.solved && (
                                        <>
                                            {resolution.passed && (
                                                <Tooltip title={getLocalizedString("approved")}>
                                                    <CheckCircleIcon
                                                        style={{
                                                            height: 20,
                                                            color: "var(--ownGreen)",
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}
                                            {resolution.passed === false && (
                                                <Tooltip title={getLocalizedString("failed")}>
                                                    <CancelIcon
                                                        style={{
                                                            height: 20,
                                                            color: "var(--ownRed)",
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}
                                            {resolution.passed === null && (
                                                <Tooltip title={getLocalizedString("toGrade")}>
                                                    <HelpIcon
                                                        style={{
                                                            height: 20,
                                                            color: "var(--ownBlue)",
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}
                                        </>
                                    )}
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            flexWrap: "wrap",
                                            columnGap: 10,
                                        }}
                                    >
                                        <Typography style={{ fontWeight: 600 }}>{resolution.name}</Typography>
                                        {resolution.solved && (
                                            <Chip
                                                size="small"
                                                label={`${getLocalizedString("attempt")} ${
                                                    resolution.currentAttempt
                                                } ${getLocalizedString("of")} ${resolution.attempts}`}
                                                style={{
                                                    border: "1px solid var(--borderColor)",
                                                }}
                                            />
                                        )}
                                        {resolution.passed !== null && (
                                            <Chip
                                                size="small"
                                                label={getLocalizedString(resolution.passed ? "approved" : "failed")}
                                                style={{
                                                    background: resolution.passed ? "var(--ownGreen)" : "var(--ownRed)",
                                                    color: "white",
                                                }}
                                            />
                                        )}
                                    </div>
                                    {!resolution.solved && (
                                        <Typography variant="caption">
                                            {getLocalizedString("activityNotSolved")}
                                        </Typography>
                                    )}
                                    {resolution.solved && (
                                        <Typography variant="caption">
                                            {resolution.score === null
                                                ? getLocalizedString("noScore")
                                                : `${getLocalizedString("score")}: ${resolution.score}`}
                                        </Typography>
                                    )}
                                </div>
                            </div>
                            {canGradeOrView && (
                                <Tooltip title={getLocalizedString("gradeOrViewActivity")} arrow>
                                    <IconButton style={{ background: color }}>
                                        <LaunchIcon style={{ fill: "white" }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Container>
                    </div>
                );
            })}
        </>
    );
};

export default Resolutions;
